<template>
  <div class="min-h-screen p-6 bg-slate-50/50">
    <div class="mx-auto w-full gap-[2rem] grid">
      <!-- Dashboard Header -->
      <DashboardHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :role="role"
        :filters="filters"
        :clients="clients"
        :service-options="serviceOptions"
        :package-options="packageOptions"
        :last-updated="lastUpdated"
        :show-package-selector="role !== 'client'"
        :date-from-min="clientDateFrom"
        :download-loading="dashboardReportOpening"
        :report-period="currentReportPeriod"
        :next-report-update="nextReportUpdate"
        :package-name="clientSummary?.package_name"
        @refresh="refresh"
        @download-report="handleDownloadDashboardReport"
        @update:filters="handleFiltersUpdate"
      />

      <!-- Error box -->
      <AnimatedBox v-if="error" orientation="y" :delay="0.05">
        <div
          class="flex flex-col gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700 shadow-sm sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p class="text-sm font-bold">
              We could not load the dashboard analytics.
            </p>
            <p class="text-xs text-rose-600 font-semibold">
              {{
                error !== "We could not load the dashboard analytics."
                  ? error
                  : "Please try again or check your network connection."
              }}
            </p>
          </div>
          <Button class="bg-indigo-600 text-white font-bold" @click="refresh">
            Try again
          </Button>
        </div>
      </AnimatedBox>

      <!-- Client specific view -->
      <template v-if="role === 'client'">
        <!-- 1. Compact Welcome Bar and Reporting Period Selector -->
        <AnimatedBox orientation="y" :delay="0.08">
          <ClientWelcomeBanner
            :company-name="
              clientSummary?.client_name ||
              authStore.clientProfile?.company_name
            "
            :needs-review-count="needsAttentionTasks.length"
            :completed-tasks-count="clientDashboardSummary?.completed || 0"
            :total-tasks-count="clientDashboardSummary?.total_tasks || 0"
            :overall-progress="overallProgressPercentage"
            :selected-period="selectedPeriod"
            @update:period="handlePeriodChange"
            @new-request="handleNewRequest"
            @contact-support="handleContactSupport"
            @review-click="scrollToNeedsAttention"
          />
        </AnimatedBox>

        <!-- 2. Key Metric Cards -->
        <DashboardMetricCards
          :summary="clientDashboardSummary"
          :loading="loading"
          :role="role"
          :period-label="periodDisplayLabel"
          :has-lead-tracking="hasLeadTracking"
          @card-click="handleMetricCardClick"
        />

        <!-- 3. Needs Your Attention -->
        <div id="needs-attention">
          <AnimatedBox orientation="y" :delay="0.12">
            <NeedsAttentionPanel
              :items="needsAttentionTasks"
              :loading="loading"
              :error="error"
              @action="handleNeedsAttentionAction"
              @retry="refresh"
            />
          </AnimatedBox>
        </div>

        <!-- 4. Your Services (Consolidated single section) -->
        <div
          id="services-overview"
          class="space-y-6 text-left flex min-w-0 w-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm p-6 md:p-8"
        >
          <!-- Heading & Filters Row -->
          <div
            class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between border-b border-slate-100/60 pb-5"
          >
            <div class="min-w-0">
              <h3 class="font-black text-slate-900 text-xl md:text-2xl">
                Your Services
              </h3>
              <p class="font-semibold text-sm md:text-base text-slate-600 mt-1">
                Active packages, deliverables, and milestone progress for your account.
              </p>
            </div>

            <!-- Search and Filter Bar for Services -->
            <div
              class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:w-auto"
            >
              <div class="relative w-full sm:col-span-2 xl:col-span-1 xl:w-64">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search services or tasks..."
                  class="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-600/10 placeholder-slate-400 bg-white"
                />
                <DMSuiteIcon
                  :icon="DASHBOARD_ICONS.search"
                  :size="16"
                  custom-class="text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"
                />
              </div>

              <select
                v-model="statusFilter"
                class="w-full p-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none bg-white text-slate-600 h-10 px-3"
              >
                <option value="all">All Statuses</option>
                <option value="needs_review">Needs Review</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on_demand">Available on Request</option>
              </select>

              <select
                v-model="sortOrder"
                class="w-full p-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none bg-white text-slate-600 h-10 px-3"
              >
                <option value="needs_attention">Action Required First</option>
                <option value="recent">Recently Active</option>
                <option value="highest">Highest Progress</option>
                <option value="lowest">Lowest Progress</option>
                <option value="az">A-Z Alphabetical</option>
              </select>
            </div>
          </div>

          <AnimatedBox orientation="y" :delay="0.15">
            <ServicesOverviewGrid
              :services="filteredServices"
              :loading="loading"
              @open-drawer="openServiceDrawer"
            />
          </AnimatedBox>
        </div>

        <!-- 5. Recent Deliveries & Updates -->
        <AnimatedBox orientation="y" :delay="0.18">
          <LatestUpdatesPanel
            :data="dashboard?.recent_activities ?? []"
            :loading="loading"
          />
        </AnimatedBox>

        <!-- 6. Latest Report -->
        <AnimatedBox orientation="y" :delay="0.2">
          <MonthlyReportsPanel
            :reports="dashboard?.reports ?? []"
            :loading="loading"
            :role="role"
            :can-view="canViewReports"
            :can-generate="canGenerateReports"
            :can-export-pdf="canExportReportPdf"
            @preview="handlePreviewReport"
            @download="handleDownloadReport"
          />
        </AnimatedBox>

        <!-- 7. Progress Details (Collapsible) -->
        <div class="space-y-4">
          <div class="flex items-center justify-between px-1">
            <div>
              <h3 class="text-lg font-black text-slate-900">Progress Details</h3>
              <p class="text-xs font-medium text-slate-500">
                Detailed workload breakdown and time-series progress across your account.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              class="text-xs font-bold text-slate-600 hover:text-slate-900"
              @click="progressDetailsCollapsed = !progressDetailsCollapsed"
            >
              {{ progressDetailsCollapsed ? 'Show Details' : 'Hide Details' }}
            </Button>
          </div>

          <div v-show="!progressDetailsCollapsed">
            <AnimatedBox orientation="y" :delay="0.22">
              <section class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <WorkloadByStatusChart
                  class="h-full bg-white border border-slate-200/80 rounded-3xl p-5 shadow-sm"
                  :data="dashboard?.workload_by_status ?? []"
                  :loading="loading"
                  :role="role"
                  :summary="dashboard?.summary ?? null"
                />
                <ProgressFlowChart
                  class="h-full bg-white border border-slate-200/80 rounded-3xl p-5 shadow-sm"
                  :data="dashboard?.progress_flow ?? []"
                  :loading="loading"
                  :role="role"
                  :summary="dashboard?.summary ?? null"
                />
              </section>
            </AnimatedBox>
          </div>
        </div>
      </template>

      <!-- Admin / Member specific view -->
      <template v-else>
        <DashboardMetricCards
          :summary="clientDashboardSummary"
          :loading="loading"
          :role="role"
        />

        <AnimatedBox orientation="y" :delay="0.15">
          <section class="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <WorkloadByStatusChart
              class="h-full"
              :data="dashboard?.workload_by_status ?? []"
              :loading="loading"
              :role="role"
              :summary="dashboard?.summary ?? null"
            />
            <ProgressFlowChart
              class="h-full"
              :data="dashboard?.progress_flow ?? []"
              :loading="loading"
              :role="role"
              :summary="dashboard?.summary ?? null"
            />
          </section>
        </AnimatedBox>

        <AnimatedBox orientation="y" :delay="0.2">
          <section class="grid grid-cols-1 gap-5">
            <AdminClientOverviewTable
              class="h-full min-w-0 w-full"
              :rows="dashboard?.client_overview ?? []"
              :loading="loading"
              @view-dashboard="handleViewDashboard"
              @view-reports="handleViewReports"
              @view-tasks="handleViewTasks"
            />
          </section>
        </AnimatedBox>

        <AnimatedBox orientation="y" :delay="0.25">
          <section>
            <ServicePackageKpiOverview
              class="h-full min-w-0 w-full"
              :data="dashboard?.service_package_kpis ?? []"
              :error="error"
              :clients="clients"
              :clients-loading="clientsLoading"
              :filters="filters"
              :loading="loading"
              :role="role"
            />
          </section>
        </AnimatedBox>

        <AnimatedBox orientation="y" :delay="0.3">
          <section class="grid gap-4 xl:grid-cols-[1fr_1.55fr]">
            <RecentActivityList
              class="h-full min-w-0 w-full"
              :data="dashboard?.recent_activities ?? []"
              :loading="loading"
              :role="role"
            />
            <!-- <ReportsAnalyticsPanel
              class="h-full min-w-0 w-full"
              :reports="dashboard?.reports ?? []"
              :loading="loading"
              :role="role"
              @download="downloadReport"
              @preview="handlePreviewReport"
              @generate-pdf="handleGeneratePdf"
            /> -->
          </section>
        </AnimatedBox>
      </template>

      <!-- Generate Report Modal -->
      <GenerateReportModal
        v-model:open="reportModalOpen"
        class="h-full min-w-0 w-full"
        :clients="clients"
        :error="reportError"
        :filters="filters"
        :loading="reportGenerating"
        :package-options="packageOptions"
        :role="role"
        :service-options="serviceOptions"
        @generate="handleGenerateReport"
      />

      <!-- Reusable Service Details Drawer -->
      <ServiceDetailsDrawer
        v-model:open="showDetailsDrawer"
        :service="selectedDrawerService"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, provide, type Component } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useAuthStore } from "@/store/auth";
import AnimatedBox from "@/components/dashboard/AnimatedBox.vue";
import GenerateReportModal from "@/components/dashboard/GenerateReportModal.vue";
import { Button } from "@/components/ui/button";
import DashboardHeader from "@/components/dashboard/DashboardHeader.vue";
import DashboardMetricCards from "@/components/dashboard/DashboardMetricCards.vue";
import WorkloadByStatusChart from "@/components/dashboard/WorkloadByStatusChart.vue";
import ProgressFlowChart from "@/components/dashboard/ProgressFlowChart.vue";
import ServicePackageKpiOverview from "@/components/dashboard/ServicePackageKpiOverview.vue";
import RecentActivityList from "@/components/dashboard/RecentActivityList.vue";
import ReportsAnalyticsPanel from "@/components/dashboard/ReportsAnalyticsPanel.vue";
import AdminClientOverviewTable from "@/components/dashboard/AdminClientOverviewTable.vue";
import NeedsAttentionPanel from "@/components/dashboard/NeedsAttentionPanel.vue";
import { useDashboardAnalytics } from "@/composables/useDashboardAnalytics";
import { useAccessControl } from "@/composables/useAccessControl";
import { ACCESS_PERMISSION_KEYS } from "@/constants/accessControl";
import http from "@/services/http";
import { createDashboardReportPreviewSnapshot } from "@/utils/dashboardReportPreview";
import {
  getPreparingReportHtml,
  getReportErrorHtml,
  writeDashboardReportPreviewWindow,
} from "@/utils/dashboardReportPreviewWindow";
import type {
  DashboardAnalyticsResponse,
  DashboardClientOverviewItem,
  DashboardFilters,
  DashboardReportDownloadPayload,
  DashboardKpiItem,
} from "@/types/dashboard";

// New Component Imports
import ClientWelcomeBanner from "@/components/dashboard/ClientWelcomeBanner.vue";
import LatestUpdatesPanel from "@/components/dashboard/LatestUpdatesPanel.vue";
import ServicesOverviewGrid from "@/components/dashboard/ServicesOverviewGrid.vue";
import ServiceDetailsDrawer from "@/components/dashboard/ServiceDetailsDrawer.vue";
import MonthlyReportsPanel from "@/components/dashboard/MonthlyReportsPanel.vue";
import { cn } from "@/lib/utils";

import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS, getServiceIconName } from "@/config/dashboardIcons";

const {
  role,
  loading,
  clientsLoading,
  dashboard,
  clients,
  filters,
  lastUpdated,
  serviceOptions,
  packageOptions,
  refresh,
  generateReport,
  error,
} = useDashboardAnalytics();
const authStore = useAuthStore();
const router = useRouter();
const toast = useToast();
const searchQuery = ref("");
const statusFilter = ref("all");
const sortOrder = ref("needs_attention");
const progressDetailsCollapsed = ref(false);

const selectedPeriod = ref<
  "this_month" | "last_month" | "last_90_days" | "all_time"
>("this_month");

const periodDisplayLabel = computed(() => {
  switch (selectedPeriod.value) {
    case "last_month":
      return "Last Month";
    case "last_90_days":
      return "Last 90 Days";
    case "all_time":
      return "All Time";
    case "this_month":
    default:
      return "This Month";
  }
});

function handlePeriodChange(
  newPeriod: "this_month" | "last_month" | "last_90_days" | "all_time"
) {
  selectedPeriod.value = newPeriod;
  const now = new Date();
  let dateFrom: string | null = null;
  let dateTo: string | null = null;

  if (newPeriod === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    dateFrom = start.toISOString().slice(0, 10);
    dateTo = now.toISOString().slice(0, 10);
  } else if (newPeriod === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    dateFrom = start.toISOString().slice(0, 10);
    dateTo = end.toISOString().slice(0, 10);
  } else if (newPeriod === "last_90_days") {
    const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    dateFrom = start.toISOString().slice(0, 10);
    dateTo = now.toISOString().slice(0, 10);
  } else if (newPeriod === "all_time") {
    dateFrom = null;
    dateTo = null;
  }

  filters.value = {
    ...filters.value,
    date_from: dateFrom,
    date_to: dateTo,
  };
  void refresh();
}

function handleNewRequest() {
  router.push({
    path: "/communication/chat",
    query: { newTicket: "true" },
  });
}

function handleContactSupport() {
  router.push("/communication/chat");
}

function scrollToNeedsAttention() {
  document
    .getElementById("needs-attention")
    ?.scrollIntoView({ behavior: "smooth" });
}

const overallProgressPercentage = computed(() => {
  const total = Number(clientDashboardSummary.value?.total_tasks) || 0;
  const completed = Number(clientDashboardSummary.value?.completed) || 0;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
});

const hasLeadTracking = computed(() => {
  const leads = clientDashboardSummary.value?.leads_captured;
  return leads !== undefined && leads !== null;
});

const selectedDrawerService = ref<any>(null);
const showDetailsDrawer = ref(false);

function openServiceDrawer(service: any) {
  selectedDrawerService.value = service;
  showDetailsDrawer.value = true;
}

function handleMetricCardClick(label: string) {
  if (label === "Needs Your Review") {
    scrollToNeedsAttention();
  } else if (label === "In Progress" || label === "Active Work") {
    router.push({
      path: "/apps/task-dashboard",
      query: { status: "in_progress" },
    });
  } else if (label.includes("Completed")) {
    router.push({
      path: "/apps/task-dashboard",
      query: { status: "completed" },
    });
  } else if (
    label === "Open Requests" ||
    label === "Open Support Requests" ||
    label === "Support Requests" ||
    label === "Support"
  ) {
    router.push("/communication/chat");
  } else if (label.includes("Leads")) {
    router.push("/leads");
  }
}

function handleDownloadReport(report: any) {
  if (!canExportReportPdf.value && !canViewReports.value) {
    toast.error("You do not have permission to download reports.");
    return;
  }
  if (report?.download_url) {
    window.open(report.download_url, "_blank");
  } else if (report?.id) {
    window.open(`/api/reports/${report.id}/download`, "_blank");
  } else {
    toast.info("Report download link is being generated.");
  }
}
const { canPermission } = useAccessControl();

const headerTitle = computed(() => "Dashboard");

const headerSubtitle = computed(() => {
  if (role.value === "super_admin") {
    return "Overall analytics across all client accounts.";
  }

  if (role.value === "admin") {
    return "Assigned-client analytics with reporting and task visibility.";
  }

  if (role.value === "member") {
    return "Member analytics with client scoping and operational visibility.";
  }

  if (role.value === "client") {
    return "Your account performance, service progress, and latest updates in one place.";
  }

  return "Analytics dashboard";
});

const currentReportPeriod = computed(() => {
  if (dashboard.value?.reports?.[0]?.report_period) {
    return dashboard.value.reports[0].report_period;
  }
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(start)} - ${fmt(end)}`;
});

const nextReportUpdate = computed(() => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 5);
  return nextMonth.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
});

const allClientTasks = computed(() => {
  const kpis = dashboard.value?.service_package_kpis ?? [];
  const tasksList: any[] = [];
  kpis.forEach((kpi) => {
    if (Array.isArray(kpi.tasks)) {
      kpi.tasks.forEach((task) => {
        tasksList.push({
          ...task,
          service_id: kpi.service_id,
          service_name: kpi.service_name,
          package_id: kpi.package_id,
          package_name: kpi.package_name,
        });
      });
    }
  });
  return tasksList;
});

const needsAttentionTasks = computed(() => {
  return allClientTasks.value.filter((task) => {
    const title = String(task.title || task.subject || "").toLowerCase();
    const status = String(task.status || "").toLowerCase();

    // 1. Exclude completed/cancelled/archived/done/closed statuses
    const excludeStatuses = [
      "closed",
      "done",
      "completed",
      "cancelled",
      "canceled",
      "archived",
    ];
    if (excludeStatuses.some((ex) => status.includes(ex))) {
      return false;
    }

    // 2. Check explicit backend fields if available
    const requiresAction =
      task.requires_client_action === true ||
      task.requires_client_action === 1 ||
      task.client_action_required === true ||
      task.client_action_required === 1 ||
      task.needs_client_action === true ||
      task.needs_client_action === 1 ||
      task.pending_client_approval === true ||
      task.pending_client_approval === 1 ||
      task.pendingClientApproval === true ||
      task.pendingClientApproval === 1 ||
      String(task.approval_status || "").toLowerCase() === "pending" ||
      String(task.approvalStatus || "").toLowerCase() === "pending";

    if (requiresAction) {
      return true;
    }

    // 3. Check task statuses
    const attentionStatuses = [
      "waiting for client",
      "client review",
      "pending approval",
      "pending feedback",
      "needs client input",
    ];
    if (attentionStatuses.some((att) => status.includes(att))) {
      return true;
    }

    // 4. Keyword fallbacks
    const needsActionKeywords = [
      "approval",
      "approve",
      "review",
      "feedback",
      "upload",
      "files",
      "provide",
    ];
    const hasKeyword = needsActionKeywords.some((keyword) =>
      title.includes(keyword)
    );

    return hasKeyword;
  });
});

const clientDashboardSummary = computed(() => {
  const baseSummary = dashboard.value?.summary;
  if (!baseSummary) return null;
  if (role.value !== "client") return baseSummary;

  return {
    ...baseSummary,
    pending_client_action: needsAttentionTasks.value.length,
  };
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleNeedsAttentionAction(item: any) {
  const taskId = item.id || item.task_id || item.taskId || item.uuid;
  const serviceId = item.service_id;
  const serviceName = item.service_name;

  router.push({
    path: "/apps/task-dashboard",
    query: {
      taskId: taskId ? String(taskId) : undefined,
      focusTask: "true",
      highlight: "true",
      serviceId: serviceId ? String(serviceId) : undefined,
      serviceName: serviceName ? String(serviceName) : undefined,
      source: "needs-attention",
    },
  });
}

const clientSummary = computed(
  () => dashboard.value?.client_overview?.[0] ?? null
);
const reportModalOpen = ref(false);
const reportGenerating = ref(false);
const reportError = ref<string | null>(null);
const dashboardReportOpening = ref(false);
const canViewReports = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_VIEW)
);
const canGenerateReports = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_GENERATE)
);
const canExportReportPdf = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.REPORTS_EXPORT_PDF)
);
const canViewTasks = computed(() =>
  canPermission(ACCESS_PERMISSION_KEYS.TASKS_VIEW)
);

const clientDateFrom = computed(() => {
  if (role.value !== "client") return null;

  const createdAt =
    authStore.clientProfile?.source_date_added ??
    authStore.clientProfile?.created_at ??
    null;

  if (!createdAt) return null;

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString().slice(0, 10);
});

function resolveSelectedClientName(clientId: string | null | undefined) {
  if (!clientId) return null;

  return (
    clients.value.find((client) => client.client_id === clientId)
      ?.company_name ??
    dashboard.value?.client_overview?.find(
      (row) => String(row.client_ref) === String(clientId)
    )?.client_name ??
    clientId
  );
}

const processedServices = computed(() => {
  if (!dashboard.value?.service_package_kpis) return [];
  return aggregateServiceProgress(dashboard.value.service_package_kpis);
});

const filteredServices = computed(() => {
  let list = processedServices.value;

  // 1. Search Query Filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase();
    list = list.filter((s) => {
      const matchName = s.label.toLowerCase().includes(query);
      const matchDesc = s.description.toLowerCase().includes(query);
      const matchTasks =
        Array.isArray(s.tasks) &&
        s.tasks.some((t: any) => {
          const title = String(
            t.title || t.name || t.subject || ""
          ).toLowerCase();
          return title.includes(query);
        });
      return matchName || matchDesc || matchTasks;
    });
  }

  // 2. Status Filter
  if (statusFilter.value !== "all") {
    list = list.filter((s) => {
      const statusKey = getServiceHealthStatus(s);
      if (statusFilter.value === "needs_review")
        return statusKey === "needs_review";
      if (statusFilter.value === "in_progress")
        return statusKey === "in_progress";
      if (statusFilter.value === "completed")
        return statusKey === "completed" || s.progress >= 100;
      if (statusFilter.value === "on_demand")
        return statusKey === "on_demand" || s.totalTasks <= 0;
      return true;
    });
  }

  // 3. Sorting
  return list.sort((a, b) => {
    if (sortOrder.value === "highest") {
      return b.progress - a.progress;
    }
    if (sortOrder.value === "lowest") {
      return a.progress - b.progress;
    }
    if (sortOrder.value === "az") {
      return a.label.localeCompare(b.label);
    }
    if (sortOrder.value === "needs_attention") {
      const scoreA = getHealthPriorityScore(a);
      const scoreB = getHealthPriorityScore(b);
      return scoreA - scoreB;
    }
    // Default 'recent'
    return b.progress - a.progress || a.label.localeCompare(b.label);
  });
});

function getServiceHealthStatus(s: any): string {
  const tasks = Array.isArray(s.tasks) ? s.tasks : [];
  if (s.totalTasks <= 0) {
    return "on_demand";
  }
  const hasPendingReview = tasks.some((t: any) => {
    const status = String(t.status || "").toLowerCase();
    const title = String(t.title || t.name || t.subject || "").toLowerCase();
    return (
      status.includes("review") ||
      status.includes("feedback") ||
      status.includes("approval") ||
      title.includes("approve") ||
      title.includes("review")
    );
  });
  if (hasPendingReview) {
    return "needs_review";
  }
  if (
    s.progress >= 100 ||
    (s.totalTasks > 0 && s.completedTasks >= s.totalTasks)
  ) {
    return "completed";
  }
  if (s.inProgressTasks > 0 || (s.progress > 0 && s.progress < 100)) {
    return "in_progress";
  }
  return "not_started";
}

function getHealthPriorityScore(s: any): number {
  const status = getServiceHealthStatus(s);
  if (status === "needs_review") return 1;
  if (status === "in_progress") return 2;
  if (status === "not_started") return 3;
  if (status === "completed") return 4;
  if (status === "on_demand") return 5;
  return 6;
}

// Aggregator Helpers
function aggregateServiceProgress(items: DashboardKpiItem[]) {
  const groups = new Map<
    string,
    {
      key: string;
      serviceId: number | string | null;
      serviceName: string;
      packageNames: Set<string>;
      status: string;
      totalTasks: number;
      completedTasks: number;
      inProgressTasks: number;
      openTasks: number;
      latestActivityAt: string | null;
      slaText: string;
      progressSeed: number;
      hasTaskData: boolean;
      tasks: any[];
    }
  >();

  for (const item of items) {
    const serviceName = String(item?.service_name || "").trim();
    if (!serviceName) continue;

    const totalTasks = safeNumber(item?.total_tasks);
    const completedTasks = safeNumber(item?.completed_tasks);
    const inProgressTasks = safeNumber(item?.in_progress_tasks);
    const openTasks =
      item?.open_tasks !== undefined && item?.open_tasks !== null
        ? safeNumber(item.open_tasks)
        : Math.max(totalTasks - completedTasks - inProgressTasks, 0);
    const progress =
      totalTasks > 0
        ? clampPercent((completedTasks / totalTasks) * 100)
        : clampPercent(item?.completion_rate ?? item?.progress_percentage);
    const key = serviceName.toLowerCase();
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key: `service-${key}`,
        serviceId: item?.service_id ?? null,
        serviceName,
        packageNames: new Set(
          [String(item?.package_name || "").trim()].filter(Boolean)
        ),
        status: String(item?.health_status || item?.status || "").trim(),
        totalTasks,
        completedTasks,
        inProgressTasks,
        openTasks,
        latestActivityAt: item?.latest_activity_at ?? null,
        slaText: String(item?.sla_status || item?.sla_deadline || "").trim(),
        progressSeed: progress,
        hasTaskData: totalTasks > 0,
        tasks: Array.isArray(item.tasks) ? [...item.tasks] : [],
      });
      continue;
    }

    existing.totalTasks += totalTasks;
    existing.completedTasks += completedTasks;
    existing.inProgressTasks += inProgressTasks;
    existing.openTasks += openTasks;
    existing.progressSeed = Math.max(existing.progressSeed, progress);
    existing.hasTaskData = existing.hasTaskData || totalTasks > 0;
    existing.latestActivityAt = maxDate(
      existing.latestActivityAt,
      item?.latest_activity_at ?? null
    );

    const packageName = String(item?.package_name || "").trim();
    if (packageName) existing.packageNames.add(packageName);
    const status = String(item?.health_status || item?.status || "").trim();
    if (status) existing.status = existing.status || status;
    const slaText = String(item?.sla_status || item?.sla_deadline || "").trim();
    if (slaText) existing.slaText = existing.slaText || slaText;
    if (Array.isArray(item.tasks)) {
      existing.tasks.push(...item.tasks);
    }
  }

  return Array.from(groups.values())
    .map((row) => {
      const progress =
        row.totalTasks > 0
          ? clampPercent((row.completedTasks / row.totalTasks) * 100)
          : clampPercent(row.progressSeed);
      const status = serviceStatusLabel(
        progress,
        row.totalTasks,
        row.completedTasks,
        row.inProgressTasks,
        row.openTasks
      );
      const packageLabel =
        row.packageNames.size === 1
          ? Array.from(row.packageNames)[0]
          : row.packageNames.size > 1
          ? "Multiple packages"
          : "Included in active plan";

      // Determine deliverable or next milestone text
      let nextDeliverable = "No active deliverables";
      if (row.totalTasks <= 0) {
        nextDeliverable = "Available on request";
      } else if (
        progress >= 100 ||
        (row.totalTasks > 0 && row.completedTasks >= row.totalTasks)
      ) {
        nextDeliverable = "Current deliverables complete";
      } else if (Array.isArray(row.tasks) && row.tasks.length > 0) {
        const inProgressTask = row.tasks.find((t: any) => {
          const st = String(t.status || "").toLowerCase();
          return (
            st.includes("progress") ||
            st.includes("review") ||
            st.includes("doing")
          );
        });
        const openTask = row.tasks.find((t: any) => {
          const st = String(t.status || "").toLowerCase();
          return (
            !st.includes("closed") &&
            !st.includes("done") &&
            !st.includes("completed")
          );
        });
        const target = inProgressTask || openTask;
        if (target) {
          nextDeliverable =
            target.title ||
            target.name ||
            target.subject ||
            "Milestone deliverable in progress";
        }
      }

      return {
        id: row.serviceId || row.key,
        key: row.key,
        label: row.serviceName,
        description: serviceDescriptionFor(row.serviceName, packageLabel),
        progress,
        completedTasks: row.completedTasks,
        inProgressTasks: row.inProgressTasks,
        openTasks: row.openTasks,
        totalTasks: row.totalTasks,
        status,
        nextDeliverable,
        taskMeta:
          row.totalTasks > 0
            ? `${row.completedTasks} of ${row.totalTasks} tasks completed`
            : row.hasTaskData
            ? "No task breakdown available yet."
            : "Service is available with no task data yet.",
        slaText: row.slaText,
        icon: getServiceIcon(row.serviceName),
        iconClass: getServiceIconClass(row.serviceName),
        iconWrapClass: getServiceIconWrapClass(row.serviceName),
        valueClass: progressValueClass(progress),
        progressClass: progressToneClass(progress),
        badgeClass: statusToneClass(status, row.totalTasks),
        tasks: row.tasks,
      };
    })
    .sort((a, b) => b.progress - a.progress || a.label.localeCompare(b.label));
}

function getServiceIcon(serviceName: string): string {
  return getServiceIconName(serviceName);
}

function getServiceIconClass(serviceName: string) {
  const value = serviceName.toLowerCase();

  if (value.includes("onboarding")) return "text-[#7c3aed]";
  if (value.includes("social media")) return "text-[#ec4899]";
  if (value.includes("ads")) return "text-[#2855a6]";
  if (value.includes("promotional")) return "text-[#f59e0b]";
  if (value.includes("chatbot") || value.includes("voice assistant"))
    return "text-[#8b5cf6]";
  if (value.includes("automation")) return "text-[#16a34a]";
  if (value.includes("seo") || value.includes("geo")) return "text-[#2855a6]";
  if (value.includes("gbp")) return "text-[#14b8a6]";
  if (value.includes("website")) return "text-[#2563eb]";
  if (value.includes("ticket") || value.includes("support"))
    return "text-[#f97316]";

  return "text-[#2855a6]";
}

function getServiceIconWrapClass(serviceName: string) {
  const value = serviceName.toLowerCase();

  if (value.includes("onboarding")) return "border-[#e6dfff] bg-[#f2ebff]";
  if (value.includes("social media")) return "border-[#fbcfe8] bg-[#fdf2f8]";
  if (value.includes("ads")) return "border-[#d8e7ff] bg-[#eef4ff]";
  if (value.includes("promotional")) return "border-[#fde68a] bg-[#fffbeb]";
  if (value.includes("chatbot") || value.includes("voice assistant"))
    return "border-[#ddd6fe] bg-[#f5f3ff]";
  if (value.includes("automation")) return "border-[#bbf7d0] bg-[#f0fdf4]";
  if (value.includes("seo") || value.includes("geo"))
    return "border-[#d8e7ff] bg-[#eef4ff]";
  if (value.includes("gbp")) return "border-[#99f6e4] bg-[#f0fdfa]";
  if (value.includes("website")) return "border-[#bfdbfe] bg-[#eff6ff]";
  if (value.includes("ticket") || value.includes("support"))
    return "border-[#fed7aa] bg-[#fff7ed]";

  return "border-[#d8e7ff] bg-[#eef4ff]";
}

function serviceDescriptionFor(serviceName: string, packageLabel: string) {
  const value = serviceName.toLowerCase();

  if (value.includes("onboarding")) return "Getting everything set up";
  if (value.includes("social media")) return "Building your brand online";
  if (value.includes("ads")) return "Running ads that convert";
  if (value.includes("promotional"))
    return "Designs that support your campaigns";
  if (value.includes("chatbot") || value.includes("voice assistant"))
    return "Smart conversations that convert";
  if (value.includes("automation")) return "Automating your lead journey";
  if (value.includes("seo") || value.includes("geo"))
    return "Improving your search visibility";
  if (value.includes("gbp")) return "Optimizing your Google presence";
  if (value.includes("website")) return "Keeping your website updated";
  if (value.includes("ticket") || value.includes("support"))
    return "Extra requests and support tickets";

  return packageLabel
    ? `Included in ${packageLabel}`
    : "Service progress for this plan";
}

function serviceStatusLabel(
  progress: number,
  totalTasks: number,
  completedTasks: number,
  inProgressTasks: number,
  openTasks: number
) {
  if (totalTasks <= 0) return "Available on request";
  if (progress >= 100 || (totalTasks > 0 && completedTasks >= totalTasks)) {
    return "Current deliverables complete";
  }
  if (inProgressTasks > 0) return "In progress";
  if (openTasks > 0 && completedTasks === 0 && inProgressTasks === 0) {
    return "Not started";
  }
  if (openTasks > 0 || completedTasks > 0) return "In progress";
  return "No work scheduled";
}

function statusToneClass(status: string, totalTasks: number) {
  const key = status.toLowerCase();

  if (key.includes("deliverables complete") || key.includes("completed")) {
    return "border-emerald-100 bg-emerald-50 text-emerald-700";
  }
  if (key.includes("in progress")) {
    return "border-blue-100 bg-blue-50 text-blue-700";
  }
  if (key.includes("available on request") || (key.includes("available") && totalTasks <= 0)) {
    return "border-cyan-100 bg-cyan-50 text-cyan-700";
  }
  if (key.includes("not started")) {
    return "border-amber-100 bg-amber-50 text-amber-700";
  }
  if (key.includes("no work scheduled")) {
    return "border-slate-200 bg-slate-50 text-slate-500";
  }

  return "border-slate-100 bg-slate-50 text-slate-600";
}

function progressToneClass(progress: number) {
  if (progress >= 100) return "bg-emerald-500";
  if (progress >= 61) return "bg-blue-500";
  if (progress >= 21) return "bg-amber-500";
  if (progress > 0) return "bg-orange-500";
  return "bg-slate-300";
}

function progressValueClass(progress: number) {
  if (progress >= 100) return "text-emerald-700";
  if (progress >= 61) return "text-blue-700";
  if (progress >= 21) return "text-amber-700";
  if (progress > 0) return "text-orange-700";
  return "text-slate-500";
}

function clampPercent(value: number | undefined | null) {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(Math.round(num), 100));
}

function safeNumber(value: unknown) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function maxDate(current: string | null, next: string | null) {
  if (!current) return next;
  if (!next) return current;

  const currentTime = new Date(current).getTime();
  const nextTime = new Date(next).getTime();
  if (Number.isNaN(currentTime)) return next;
  if (Number.isNaN(nextTime)) return current;

  return nextTime > currentTime ? next : current;
}

function resolveReportClientId(payload: DashboardReportDownloadPayload) {
  if (payload.action !== "specific") {
    return null;
  }

  const selected = String(payload.client_id || "").trim();
  if (!selected || selected === "all") {
    return null;
  }

  return selected;
}

function buildReportQueryParams(payload: DashboardReportDownloadPayload) {
  const params: Record<string, string | number> = {
    report_scope: payload.action,
  };

  const clientId = resolveReportClientId(payload);
  if (clientId) {
    params.client_id = clientId;
  }

  if (payload.filters.service_id !== null) {
    params.service_id = payload.filters.service_id;
  }

  if (payload.filters.package_id !== null) {
    params.package_id = payload.filters.package_id;
  }

  if (payload.filters.date_from) {
    params.date_from = payload.filters.date_from;
  }

  if (payload.filters.date_to) {
    params.date_to = payload.filters.date_to;
  }

  return params;
}

function resolveReportScopeLabel(payload: DashboardReportDownloadPayload) {
  if (payload.action === "all") {
    return "All Accounts";
  }

  if (payload.action === "assigned") {
    return "Assigned Accounts";
  }

  if (payload.action === "specific") {
    return "Specific Account";
  }

  return "Current Account";
}

function resolveReportAccountName(
  payload: DashboardReportDownloadPayload,
  data: DashboardAnalyticsResponse
) {
  if (payload.action === "specific") {
    return (
      resolveSelectedClientName(resolveReportClientId(payload)) ??
      data.client_overview?.[0]?.client_name ??
      payload.client_id ??
      "Selected account"
    );
  }

  if (payload.action === "all") {
    return "All accessible accounts";
  }

  if (payload.action === "assigned") {
    return "Assigned accounts";
  }

  return (
    authStore.companyName ||
    authStore.clientProfile?.company_name ||
    data.client_overview?.[0]?.client_name ||
    "Current account"
  );
}

function buildReportGeneratedBy() {
  return (
    authStore.internalUser?.alias ||
    authStore.internalUser?.name ||
    authStore.clientProfile?.company_name ||
    authStore.displayEmail ||
    "System"
  );
}

function buildFiltersLabel(payload: DashboardReportDownloadPayload) {
  const parts: string[] = [];

  if (payload.filters.service_id !== null) {
    const serviceLabel =
      serviceOptions.value.find(
        (option) => option.value === String(payload.filters.service_id)
      )?.label ?? `Service ${payload.filters.service_id}`;
    parts.push(`Service: ${serviceLabel}`);
  }

  if (payload.filters.package_id !== null) {
    const packageLabel =
      packageOptions.value.find(
        (option) => option.value === String(payload.filters.package_id)
      )?.label ?? `Package ${payload.filters.package_id}`;
    parts.push(`Package: ${packageLabel}`);
  }

  if (payload.action === "specific" && payload.client_id) {
    parts.push(
      `Account: ${
        resolveSelectedClientName(payload.client_id) ?? payload.client_id
      }`
    );
  }

  return parts.length ? parts.join(" - ") : null;
}

function buildDateRangeLabel(payload: DashboardReportDownloadPayload) {
  if (!payload.filters.date_from && !payload.filters.date_to) {
    return null;
  }

  if (payload.filters.date_from && payload.filters.date_to) {
    return `${payload.filters.date_from} - ${payload.filters.date_to}`;
  }

  if (payload.filters.date_from) {
    return `From ${payload.filters.date_from}`;
  }

  return `To ${payload.filters.date_to}`;
}

async function handleDownloadDashboardReport(
  payload: DashboardReportDownloadPayload
) {
  if (!canExportReportPdf.value) {
    toast.error("You do not have access to export reports.");
    return;
  }

  if (dashboardReportOpening.value) {
    return;
  }

  if (loading.value) {
    toast.info("Please wait until the dashboard finishes loading.");
    return;
  }

  dashboardReportOpening.value = true;
  const reportWindow = window.open("", "_blank");

  if (!reportWindow) {
    dashboardReportOpening.value = false;
    toast.error("Unable to open report. Please allow popups and try again.");
    return;
  }

  reportWindow.document.open();
  reportWindow.document.write(getPreparingReportHtml());
  reportWindow.document.close();
  reportWindow.focus();

  try {
    if (payload.action === "specific" && !resolveReportClientId(payload)) {
      throw new Error(
        "Please select a client account before generating this report."
      );
    }

    const params = buildReportQueryParams(payload);
    const { data } = await http.get<DashboardAnalyticsResponse>(
      "/api/dashboard/analytics",
      {
        params,
      }
    );

    if (reportWindow.closed) {
      return;
    }

    const snapshot = createDashboardReportPreviewSnapshot({
      action: payload.action,
      generatedBy: buildReportGeneratedBy(),
      accountName: resolveReportAccountName(payload, data),
      scopeLabel: resolveReportScopeLabel(payload),
      reportTitle: "Dashboard Report",
      headerSubtitle:
        payload.action === "specific"
          ? `Dashboard analytics for ${resolveReportAccountName(payload, data)}`
          : payload.action === "all"
          ? "Dashboard analytics for all accessible accounts"
          : payload.action === "assigned"
          ? "Dashboard analytics for assigned accounts"
          : "Dashboard analytics for the current account",
      filtersLabel: buildFiltersLabel(payload),
      dateRangeLabel: buildDateRangeLabel(payload),
      serviceLabel:
        payload.filters.service_id !== null
          ? serviceOptions.value.find(
              (option) => option.value === String(payload.filters.service_id)
            )?.label ?? null
          : null,
      packageLabel:
        payload.filters.package_id !== null
          ? packageOptions.value.find(
              (option) => option.value === String(payload.filters.package_id)
            )?.label ?? null
          : null,
      role: payload.role,
      summary: data.summary ?? null,
      workloadByStatus: data.workload_by_status ?? [],
      progressFlow: data.progress_flow ?? [],
      servicePackageKpis: data.service_package_kpis ?? [],
      latestReport: data.reports?.[0] ?? null,
    });

    writeDashboardReportPreviewWindow(reportWindow, snapshot);
  } catch (error) {
    console.error("Dashboard report preview failed:", error);

    if (!reportWindow.closed) {
      reportWindow.document.open();
      reportWindow.document.write(getReportErrorHtml(error));
      reportWindow.document.close();
    }

    toast.error("Unable to generate report preview. Please try again.");
  } finally {
    dashboardReportOpening.value = false;
  }
}

function handleGeneratePdf() {
  if (!canGenerateReports.value) {
    toast.error("You do not have access to generate reports.");
    return;
  }

  reportError.value = null;
  reportModalOpen.value = true;
}

function handleOpenServicePlan() {
  router.push("/client-profile");
}

function handlePreviewReport(report: { preview_url?: string | null }) {
  if (!canViewReports.value) {
    toast.error("You do not have access to view reports.");
    return;
  }

  if (report.preview_url) {
    window.open(report.preview_url, "_blank");
  }
}

function handleViewDashboard(row: DashboardClientOverviewItem) {
  filters.value = {
    ...filters.value,
    client_id: row.client_ref,
  };
  void refresh();
}

function handleViewReports(row: DashboardClientOverviewItem) {
  if (!canViewReports.value) {
    toast.error("You do not have access to view reports.");
    return;
  }

  filters.value = {
    ...filters.value,
    client_id: row.client_ref,
  };
  document
    .querySelector('[data-dashboard-section="reports"]')
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function handleViewTasks() {
  if (!canViewTasks.value) {
    toast.error("You do not have access to view tasks.");
    return;
  }

  router.push("/apps/task-dashboard");
}

function handleFiltersUpdate(value: DashboardFilters) {
  filters.value = value;
}

async function handleGenerateReport(payload: {
  client_id: string;
  service_id: number | null;
  package_id: number | null;
  date_from: string | null;
  date_to: string | null;
  include_appendix: boolean;
  supporting_resources: File[];
  mode: "html" | "pdf";
}) {
  if (!canGenerateReports.value) {
    toast.error("You do not have access to generate reports.");
    return;
  }

  reportGenerating.value = true;
  reportError.value = null;
  const reportWindow = window.open("", "_blank");

  try {
    const snapshot = await generateReport(payload);
    reportModalOpen.value = false;

    if (payload.mode === "html") {
      if (reportWindow) {
        reportWindow.location.href = snapshot.preview_url;
      } else {
        window.location.assign(snapshot.preview_url);
      }
      return;
    }

    if (reportWindow) {
      reportWindow.location.href = snapshot.download_url;
    } else {
      window.open(snapshot.download_url, "_blank");
    }
  } catch (error) {
    reportError.value =
      error instanceof Error
        ? error.message
        : "Unable to generate the report right now.";
    if (reportWindow) {
      reportWindow.close();
    }
  } finally {
    reportGenerating.value = false;
  }
}

watch(
  () => dashboard.value?.scope?.client_id,
  (currentClientId) => {
    if (role.value !== "client") {
      return;
    }

    if (
      currentClientId &&
      filters.value.client_id !== currentClientId &&
      currentClientId !== "all"
    ) {
      filters.value.client_id = currentClientId;
    }
  }
);
</script>
